/**
 * RUNNING TECHNIQUE DRILLS
 *
 * This file contains consolidated running technique drills for kids ages 6-10.
 * Each drill is a 30-60 minute session focusing on proper running form, speed, and agility.
 *
 * DRILL STRUCTURE:
 * - id: Numeric ID starting from 2110 (running technique drills)
 * - name: Clean drill name (no emojis - for TTS audio generation)
 * - categories: Array of skill categories
 * - sports: Array containing 'running-technique'
 * - difficulty: 'beginner', 'intermediate', or 'advanced'
 * - skill: Skill category name
 * - points: 30 (beginner), 45 (intermediate/advanced), or 60 (complete)
 * - instructions: Detailed session description
 * - timedInstructions: Array of timed instruction objects
 * - timeTotal: Total session time in seconds
 * - equipment: Required equipment (minimal for home use)
 * - requiresPartner: false (individual drills)
 * - minPlayers: 1
 */

const runningTechniqueDrills = [
  // BEGINNER RUNNING FORM (30 minutes)
  {
    id: 2110,
    name: 'Running Form',
    categories: ['Form'],
    sports: ['running-technique'],
    difficulty: 'beginner',
    skill: 'Form',
    points: 18,
    instructions:
      'Running Form teaches you the key habits for smooth, safe, and fast running. This 30-minute session builds posture, arm swing, midfoot land, ankle lean, and quick steps, with optional challenge blocks you can try when ready.',
    timedInstructions: [
      // 0:00-3:00 Arrive, posture, breath
      {
        time: 0,
        text: 'Stand tall in Ready. Feet on two tracks, knees soft, head over hips. Breathe in your nose, out your mouth.',
      },
      {
        time: 12,
        text: 'Posture definition. Tall spine, chest calm, tummy snug, shoulders down, eyes forward.',
      },
      {
        time: 24,
        text: 'Feet check. Toes point straight ahead. Knees over toes.',
      },
      {
        time: 36,
        text: 'Arms easy. Elbows by your sides, hands soft like you hold a chip.',
      },
      { time: 48, text: 'Breathe in for 3, 2, 1. Breathe out for 3, 2, 1.' },
      { time: 60, text: 'Ankles roll. Right two, left two. Quiet feet.' },
      {
        time: 72,
        text: 'March in place. Lift knees tiny. Land soft under hips.',
      },
      {
        time: 84,
        text: 'Posture reminder. Head over hips, hips over feet. Two tracks.',
      },
      { time: 96, text: 'Stand tall. Hold still for 3, 2, 1.' },
      { time: 108, text: 'Neck long, jaw soft, shoulders down.' },
      { time: 120, text: 'Breathe steady. Small smile.' },
      { time: 132, text: 'Ready to move with form.' },

      // 3:00-6:00 Foot placement and midfoot feel
      {
        time: 144,
        text: 'Walk slow in place. Step under your body. Feet on two tracks.',
      },
      {
        time: 156,
        text: 'Foot cue. Land on the middle of your foot, then roll to the front. Heels kiss the ground lightly.',
      },
      {
        time: 168,
        text: 'Walk and feel. Midfoot land, roll forward, soft step.',
      },
      {
        time: 180,
        text: 'Freeze. Balance on one foot for 3, 2, 1. Switch feet for 3, 2, 1.',
      },
      {
        time: 192,
        text: 'Two tracks reminder. Do not cross your feet. Toes forward.',
      },
      { time: 204, text: 'Walk again. Quiet feet. Head over hips.' },
      { time: 216, text: 'Short steps under hips. No reaching far.' },
      { time: 228, text: 'Stand tall. Breathe steady.' },

      // 6:00-9:00 Arm swing mechanics
      {
        time: 240,
        text: 'Arm swing definition. Elbows bent about a right angle. Hands swing cheek to pocket along your sides.',
      },
      {
        time: 252,
        text: 'Swing arms only. Cheek to pocket. Thumbs brush your hip line.',
      },
      { time: 264, text: 'Shoulders down. Hands soft. No fist squeeze.' },
      {
        time: 276,
        text: 'Add march. March tiny with arm swing. Keep elbows close to ribs.',
      },
      { time: 288, text: 'Reminder. Arms drive the legs. Smooth swing.' },
      { time: 300, text: 'Freeze. Check posture. Tall spine, eyes forward.' },
      { time: 312, text: 'March and swing. Quiet feet. Breathe out soft.' },
      { time: 324, text: 'Stand easy. Inhale, exhale.' },

      // 9:00-12:00 Lean from ankles
      {
        time: 336,
        text: 'Lean definition. Body is a straight line that tips a tiny bit from the ankles, not from the waist.',
      },
      {
        time: 348,
        text: 'Try a tiny lean forward. Heels light. Head over hips.',
      },
      { time: 360, text: 'Release lean. Stand tall. Feel the change.' },
      {
        time: 372,
        text: 'Lean and march. Tiny lean, tiny march. Steps under hips.',
      },
      {
        time: 384,
        text: 'Reminder. Straight line from ears to ankles. Do not fold at the waist.',
      },
      { time: 396, text: 'Lean with arm swing. Light march, smooth arms.' },
      { time: 408, text: 'Freeze. Hold tall for 3, 2, 1.' },
      { time: 420, text: 'Breathe steady. Quiet feet.' },

      // 12:00-15:00 Cadence feel
      {
        time: 432,
        text: 'Cadence definition. Cadence means quick light steps that are small and even.',
      },
      {
        time: 444,
        text: 'Quick feet drill. Fast tiny march for 4 counts. Then relax.',
      },
      { time: 456, text: 'Repeat quick tiny march. 4 counts fast, then easy.' },
      { time: 468, text: 'Add arm swing with quick feet. Keep arms close.' },
      { time: 480, text: 'Reminder. Small steps. Land under you. Quiet feet.' },
      { time: 492, text: 'Lean tiny with quick feet. Head over hips.' },
      { time: 504, text: 'Freeze. Two tracks, knees soft.' },
      { time: 516, text: 'Breath cue. In through nose, out through mouth.' },

      // 15:00-18:00 Drills: high knees and butt kicks light
      {
        time: 528,
        text: 'High knees definition. Lift knees small in front while arms swing. Land soft under hips.',
      },
      {
        time: 540,
        text: 'Do high knees light and low for 6 counts. Quiet feet.',
      },
      { time: 552, text: 'Reminder. Small lifts. Chest calm. Eyes forward.' },
      {
        time: 564,
        text: 'Butt kicks definition. Heels flick up softly behind you. Knees point down, steps small.',
      },
      {
        time: 576,
        text: 'Do butt kicks light for 6 counts. Arms swing smooth.',
      },
      {
        time: 588,
        text: 'Alternate. 4 counts high knees, 4 counts butt kicks.',
      },
      {
        time: 600,
        text: 'Posture check. Tall spine, head over hips, two tracks.',
      },
      { time: 612, text: 'Breathe steady. Quiet landings.' },

      // 18:00-21:00 A-march to A-skip
      {
        time: 624,
        text: 'A-march definition. March with knee up front, toe up, heel under knee, then place the foot under your body.',
      },
      {
        time: 636,
        text: 'A-march right then left. Arms swing cheek to pocket.',
      },
      {
        time: 648,
        text: 'Reminder. Toe up, place down under hips. Small step.',
      },
      {
        time: 660,
        text: 'A-skip definition. Same knee action with a tiny hop. Light and quick.',
      },
      { time: 672, text: 'A-skip slow. Right then left. Quiet feet.' },
      { time: 684, text: 'A-skip steady. Small hop, smooth arms.' },
      { time: 696, text: 'Freeze tall for 3, 2, 1. Breathe out.' },
      { time: 708, text: 'Two tracks. Knees over toes.' },

      // 21:00-24:00 B-skip and straight leg run
      {
        time: 720,
        text: 'B-skip definition. Lift knee, extend lower leg forward, then pull foot down under your body.',
      },
      { time: 732, text: 'B-skip slow. Right then left. Arms swing smooth.' },
      {
        time: 744,
        text: 'Reminder. Extend then pull down under hips. Small and quick.',
      },
      {
        time: 756,
        text: 'Straight leg run definition. Legs straight in front with quick ankle snaps, land under body.',
      },
      {
        time: 768,
        text: 'Straight leg run small. Quick snaps, soft landings.',
      },
      {
        time: 780,
        text: 'Alternate. 4 counts B-skip, 4 counts straight legs.',
      },
      {
        time: 792,
        text: 'Posture check. Lean tiny from ankles, not the waist.',
      },
      { time: 804, text: 'Breathe steady. Hands soft.' },

      // 24:00-26:00 Ankling and pop-off
      {
        time: 816,
        text: 'Ankling definition. Tiny fast steps where ankles bounce like springs. Steps under hips.',
      },
      { time: 828, text: 'Ankling for 6 counts. Quiet feet, quick steps.' },
      { time: 840, text: 'Reminder. Small steps. Hips tall. Arms close.' },
      {
        time: 852,
        text: 'Pop-off definition. Small light bounce straight up, then step under you. Hips tall.',
      },
      { time: 864, text: 'Pop-off for 4 counts, then ankling for 4 counts.' },
      {
        time: 876,
        text: 'Combine. Ankling, A-skip, butt kicks, high knees. 2 counts each.',
      },

      // 26:00-28:00 Challenge block: cadence ladders and strides
      {
        time: 888,
        text: 'Challenge. Cadence ladder. Quick feet 4 counts, then 6, then 8. Small steps.',
      },
      {
        time: 900,
        text: 'Strides definition. Short fast bursts with smooth arms and soft landings, then easy jog in place.',
      },
      {
        time: 912,
        text: 'Stride in place for 4 counts fast, then 4 counts easy. Do two rounds.',
      },
      {
        time: 924,
        text: 'Reminder. Land under you. Midfoot first. Arms drive.',
      },
      {
        time: 936,
        text: 'Lean tiny, quick feet for 6 counts, then stand tall and breathe.',
      },
      { time: 948, text: 'Balance check. Hold tall for 3, 2, 1. Two tracks.' },

      // 28:00-29:00 Challenge block: hill simulation
      {
        time: 960,
        text: 'Hill simulation definition. A tiny extra ankle lean, knees lift a little, arms pump a little more.',
      },
      {
        time: 972,
        text: 'Hill march 6 counts. Lean tiny. Pump arms. Quiet feet.',
      },
      {
        time: 984,
        text: 'Hill run in place 6 counts. Small steps. Breathe steady.',
      },
      { time: 996, text: 'Stand tall. Head over hips. Shoulders down.' },

      // 29:00-30:00 Cooldown and finish
      { time: 1008, text: 'Shake arms. Shake legs. Stand tall.' },
      {
        time: 1020,
        text: 'Calf stretch small. Step one foot back, heel heavy, hold for 3, 2, 1. Switch.',
      },
      {
        time: 1032,
        text: 'Thigh stretch small. Heel to bottom, hold shoe or pant leg if safe, hold for 3, 2, 1. Switch.',
      },
      { time: 1044, text: 'Ankles roll. Right two, left two. Quiet feet.' },
      { time: 1056, text: 'Breathe in slow. Breathe out slow.' },
      {
        time: 1068,
        text: 'Posture scan. Tall spine, head over hips, shoulders down.',
      },
      {
        time: 1080,
        text: 'Coach kudos. Smooth arms, quick feet, soft landings.',
      },
      { time: 1092, text: 'Stand proud. Hold still for 3, 2, 1.' },
      {
        time: 1104,
        text: 'Deep breath in. Long breath out. Session complete. Great job today!',
      },
    ],
    timeTotal: 1108,
    equipment: 'None',
    requiresPartner: false,
    minPlayers: 1,
  },

  // BEGINNER SPEED & AGILITY (30 minutes)
  {
    id: 2113,
    name: 'Speed & Agility',
    categories: ['Speed', 'beginner'],
    sports: ['running-technique'],
    difficulty: 'beginner',
    skill: 'Speed',
    points: 18,
    instructions:
      'Speed and Agility teaches you the habits for quick feet, fast starts, and smooth direction changes. This 18-minute session builds athletic stance, arm drive, quick steps, and safe cuts, with optional challenge blocks you can try when ready.',
    // Replace timedInstructions with:
    timedInstructions: [
      // 0:00-3:00 Arrive, stance, breath
      {
        time: 0,
        text: 'Stand tall in Athletic Stance. Feet on two tracks, knees soft, hips back a little, chest calm, eyes up.',
      },
      {
        time: 12,
        text: 'Posture definition. Tall spine, head over hips, shoulders down, tummy snug, arms ready by your sides.',
      },
      {
        time: 24,
        text: 'Breathe in your nose for 3, 2, 1. Breathe out your mouth for 3, 2, 1.',
      },
      { time: 36, text: 'Ankles roll. Right two, left two. Quiet feet.' },
      {
        time: 48,
        text: 'March in place. Lift knees tiny. Land soft under hips.',
      },
      {
        time: 60,
        text: 'Arms ready. Elbows bent, hands soft, swing cheek to pocket.',
      },
      {
        time: 72,
        text: 'Posture check. Two tracks, knees over toes, tall spine.',
      },
      { time: 84, text: 'Hold Athletic Stance for 3, 2, 1. Calm breath.' },
      { time: 96, text: 'Shake arms. Shake legs. Stand ready.' },
      { time: 108, text: 'Eyes forward. Small smile.' },
      { time: 120, text: 'Two tracks under feet. Quiet feet.' },
      { time: 132, text: 'We move small to stay safe and clean.' },

      // 3:00-6:00 Quick feet base
      {
        time: 144,
        text: 'Quick feet definition. Fast tiny steps in place with soft landings under your hips.',
      },
      { time: 156, text: 'Quick feet 4 counts, then freeze. Do it now.' },
      {
        time: 168,
        text: 'Repeat quick feet 4 counts, then freeze. Arms swing smooth.',
      },
      {
        time: 180,
        text: 'Two-foot line hops definition. Imagine a line under you. Hop two feet side to side small.',
      },
      {
        time: 192,
        text: 'Hop side to side 4 counts, then freeze. Quiet feet.',
      },
      {
        time: 204,
        text: 'Forward back hops small over the line 4 counts, then freeze.',
      },
      { time: 216, text: 'Reminder. Knees soft, land under you, heels light.' },
      { time: 228, text: 'Hold Athletic Stance for 3, 2, 1.' },

      // 6:00-9:00 Lateral shuffle
      {
        time: 240,
        text: 'Shuffle definition. Step and slide to the side, feet keep the same width, hips low, chest calm.',
      },
      {
        time: 252,
        text: 'Shuffle left three small steps, then right three. Hands ready.',
      },
      { time: 264, text: 'Repeat shuffles. Left then right. Quiet feet.' },
      {
        time: 276,
        text: 'Add stop. Shuffle left and stop in stance for 1. Then right and stop.',
      },
      { time: 288, text: 'Reminder. Two tracks, knees over toes, eyes up.' },
      {
        time: 300,
        text: 'Shuffle left, freeze 1. Shuffle right, freeze 1. One more time.',
      },
      { time: 312, text: 'Breath cue. In through nose, out through mouth.' },
      { time: 324, text: 'Stand tall. Reset.' },

      // 9:00-12:00 Forward and back cuts
      {
        time: 336,
        text: 'Drop step definition. A tiny back step to make space before you move.',
      },
      {
        time: 348,
        text: 'Start stance. Step forward two small steps, then back two small steps.',
      },
      {
        time: 360,
        text: 'Add cuts. Forward two, plant, back two. Keep steps short.',
      },
      {
        time: 372,
        text: 'Stop start definition. Stop in stance, then start fast with quick feet.',
      },
      {
        time: 384,
        text: 'Do stop start. Freeze, then quick feet 4 counts. Repeat once.',
      },
      { time: 396, text: 'Reminder. Land under you. Heels light. Arms drive.' },
      { time: 408, text: 'Forward two, back two, freeze. One more time.' },
      { time: 420, text: 'Stand tall. Breathe steady.' },

      // 12:00-15:00 Agility patterns
      {
        time: 432,
        text: 'Box pattern definition. Forward, right, back, left. Small steps.',
      },
      { time: 444, text: 'Run the box one time. Quiet feet.' },
      { time: 456, text: 'Run the box again. Freeze in stance.' },
      {
        time: 468,
        text: 'Zigzag definition. Two small diagonal steps right, then two left.',
      },
      { time: 480, text: 'Do zigzag once. Hips low, chest calm.' },
      { time: 492, text: 'Do zigzag again. Arms swing small.' },
      {
        time: 504,
        text: 'Reminder. Two tracks, knees over toes, head over hips.',
      },
      { time: 516, text: 'Hold stance for 3, 2, 1.' },

      // 15:00-18:00 Reaction game 1
      { time: 528, text: 'Coach call. When you hear it, you do it. Ready.' },
      { time: 540, text: 'Left.' },
      { time: 552, text: 'Right.' },
      { time: 564, text: 'Forward.' },
      { time: 576, text: 'Back.' },
      { time: 588, text: 'Zigzag.' },
      { time: 600, text: 'Box.' },
      { time: 612, text: 'Freeze stance for 3, 2, 1. Quiet feet.' },

      // 18:00-21:00 Speed mechanics
      {
        time: 624,
        text: 'Lean definition. A tiny ankle lean forward, body in a straight line, not from the waist.',
      },
      { time: 636, text: 'Lean and quick feet 4 counts. Then stand tall.' },
      {
        time: 648,
        text: 'Arm drive definition. Elbows swing cheek to pocket close to your sides.',
      },
      {
        time: 660,
        text: 'Arm drive with quick feet 4 counts. Breathe out soft.',
      },
      {
        time: 672,
        text: 'Acceleration drill. Lean tiny, quick feet 6 counts, then freeze.',
      },
      { time: 684, text: 'Reminder. Small steps. Land under you. Arms drive.' },
      { time: 696, text: 'Do it again. Lean tiny, quick feet 6 counts.' },
      { time: 708, text: 'Stand tall. Calm breath.' },

      // 21:00-24:00 Single leg control
      {
        time: 720,
        text: 'Single leg hop definition. Small soft hops on one foot, land under hips.',
      },
      {
        time: 732,
        text: 'Right foot hops 4, then freeze. Left foot hops 4, then freeze.',
      },
      { time: 744, text: 'Repeat right 4, left 4. Quiet feet.' },
      {
        time: 756,
        text: 'Skaters definition. Side step and light hop to the other foot, small distance, land soft.',
      },
      { time: 768, text: 'Do skaters 4 counts. Arms swing small.' },
      {
        time: 780,
        text: 'Balance hold. Stand on right foot 3, 2, 1. Left foot 3, 2, 1.',
      },
      { time: 792, text: 'Reminder. Knees soft, hips level, eyes forward.' },
      { time: 804, text: 'Hold stance for 3, 2, 1.' },

      // 24:00-26:00 Challenge block 1: ladder patterns (no ladder needed)
      {
        time: 816,
        text: 'Pattern definition. In in out out means fast feet step in twice then out twice.',
      },
      { time: 828, text: 'Do in in out out 4 counts, then freeze.' },
      {
        time: 840,
        text: 'Icky shuffle definition. In with one foot, in with the other, out to the side. Small and quick.',
      },
      { time: 852, text: 'Do icky shuffle 4 counts, then freeze.' },
      { time: 864, text: 'Repeat in in out out 4, then icky shuffle 4.' },
      { time: 876, text: 'Reminder. Small steps, heels light, arms close.' },

      // 26:00-28:00 Challenge block 2: multi change-of-direction
      {
        time: 888,
        text: 'Chain. Forward two, cut right, back two, cut left, freeze stance.',
      },
      { time: 900, text: 'Repeat the chain. Quiet feet.' },
      {
        time: 912,
        text: 'Stride in place definition. Short fast burst, then easy.',
      },
      { time: 924, text: 'Stride in place fast 4 counts, then easy 4 counts.' },
      { time: 936, text: 'Repeat fast 4, easy 4. Arms drive.' },
      {
        time: 948,
        text: 'Reminder. Two tracks, knees over toes, head over hips.',
      },

      // 28:00-30:00 Cooldown and finish
      { time: 960, text: 'Shake arms. Shake legs. Stand tall.' },
      {
        time: 972,
        text: 'Calf stretch small. Step one foot back, heel heavy, hold for 3, 2, 1. Switch.',
      },
      {
        time: 984,
        text: 'Thigh stretch small. Heel to bottom if safe, hold for 3, 2, 1. Switch.',
      },
      { time: 996, text: 'Ankles roll. Right two, left two. Quiet feet.' },
      { time: 1008, text: 'Breathe in slow. Breathe out slow.' },
      {
        time: 1020,
        text: 'Posture scan. Tall spine, head over hips, shoulders down.',
      },
      { time: 1032, text: 'Coach kudos. Quick feet, safe cuts, smooth arms.' },
      { time: 1044, text: 'Stand proud. Hold still for 3, 2, 1.' },
      {
        time: 1056,
        text: 'Deep breath in. Long breath out. Session complete. Speed and Agility finished. Great job today.',
      },
    ],
    timeTotal: 1060,
    equipment: 'None',
    requiresPartner: false,
    minPlayers: 1,
  },

  // BEGINNER ENDURANCE (30 minutes)
  {
    id: 2116,
    name: 'Beginner Endurance',
    categories: ['Endurance', 'beginner'],
    sports: ['running-technique'],
    difficulty: 'beginner',
    skill: 'Endurance',
    points: 22,
    instructions:
      'Beginner Endurance teaches you how to run longer with smooth form and calm breathing. This 30-minute session builds posture, arm swing, midfoot land, ankle lean, and steady pace with simple run and walk intervals and kid-friendly tips.',
    // Replace timedInstructions with:
    timedInstructions: [
      // 0:00-3:00 Arrive, posture, breath
      {
        time: 0,
        text: 'Stand tall in Ready. Feet on two tracks, knees soft, head over hips. Breathe in your nose, out your mouth.',
      },
      {
        time: 12,
        text: 'Posture definition. Tall spine, chest calm, tummy snug, shoulders down, eyes forward.',
      },
      {
        time: 24,
        text: 'Feet check. Toes point straight ahead. Knees over toes.',
      },
      {
        time: 36,
        text: 'Arms easy. Elbows bent, hands soft, swing cheek to pocket along your sides.',
      },
      { time: 48, text: 'Breathe in for 3, 2, 1. Breathe out for 3, 2, 1.' },
      { time: 60, text: 'Ankles roll. Right two, left two. Quiet feet.' },
      {
        time: 72,
        text: 'March in place. Lift knees tiny. Land soft under hips.',
      },
      {
        time: 84,
        text: 'Posture reminder. Head over hips, hips over feet. Two tracks.',
      },
      { time: 96, text: 'Stand tall. Hold still for 3, 2, 1.' },
      { time: 108, text: 'Neck long, jaw soft, shoulders down.' },
      { time: 120, text: 'Breathe steady. Small smile.' },
      { time: 132, text: 'Ready to move with form.' },

      // 3:00-6:00 Walk warm up and breathing rhythm
      {
        time: 144,
        text: 'Start a gentle walk in place. Steps under your body. Arms swing smooth.',
      },
      {
        time: 156,
        text: 'Breathing pattern. In for 3, out for 3. Keep it steady.',
      },
      { time: 168, text: 'Walk and breathe. Quiet feet. Eyes forward.' },
      { time: 180, text: 'Two tracks under feet. Toes forward. Knees soft.' },
      { time: 192, text: 'Stand tall for 3, 2, 1. Then walk again.' },
      { time: 204, text: 'Shoulders down. Hands soft. Calm face.' },
      { time: 216, text: 'Walk a little faster. Short steps under hips.' },
      {
        time: 228,
        text: 'Breathe in nose, breathe out mouth. Smooth and calm.',
      },

      // 6:00-9:00 Form basics for endurance
      {
        time: 240,
        text: 'Midfoot land definition. Touch the middle of your foot first, then roll to the front. Heels kiss the ground.',
      },
      { time: 252, text: 'Practice midfoot. Step and roll. Quiet feet.' },
      {
        time: 264,
        text: 'Arm swing reminder. Cheek to pocket, elbows close to ribs.',
      },
      {
        time: 276,
        text: 'Lean definition. Tip a tiny bit from ankles, not from your waist. Body stays like a straight line.',
      },
      {
        time: 288,
        text: 'Try tiny lean with walking. Heels light, steps small.',
      },
      {
        time: 300,
        text: 'Cadence definition. Quick small steps that are even and light.',
      },
      { time: 312, text: 'Quick steps for 4 counts, then easy walk.' },
      { time: 324, text: 'Stand tall. Hold for 3, 2, 1. Calm breath.' },

      // 9:00-12:00 Easy jog set 1
      {
        time: 336,
        text: 'Start an easy jog in place. Conversation pace means you can talk while you jog.',
      },
      {
        time: 348,
        text: 'Form check. Tall spine, tiny ankle lean, small steps under hips.',
      },
      { time: 360, text: 'Feet quiet. Land midfoot and roll forward.' },
      { time: 372, text: 'Arms smooth. Cheek to pocket. Hands soft.' },
      { time: 384, text: 'Breathing. In for 3, out for 3. Calm and steady.' },
      { time: 396, text: 'Eyes forward. Head over hips. Shoulders down.' },
      { time: 408, text: 'Hold easy pace. Quiet landings.' },
      { time: 420, text: 'Stand tall. Walk easy for 20 seconds.' },

      // 12:00-15:00 Easy walk and reset
      {
        time: 432,
        text: 'Walk easy. Posture scan. Two tracks, knees over toes, tall spine.',
      },
      { time: 444, text: 'Breathe in for 3, out for 3. Arms swing smooth.' },
      { time: 456, text: 'Midfoot walk. Step under body, roll to the front.' },
      { time: 468, text: 'Tiny ankle lean. Heels light. Head over hips.' },
      { time: 480, text: 'Stand tall. Hold for 3, 2, 1. Calm breath.' },
      { time: 492, text: 'Ready for the next jog. Small smile.' },
      { time: 504, text: 'Two tracks under feet. Toes forward.' },
      { time: 516, text: 'Arms ready. Hands soft.' },

      // 15:00-18:00 Easy jog set 2
      { time: 528, text: 'Start easy jog again. Keep steps small and quick.' },
      { time: 540, text: 'Form check. Tall spine, tiny ankle lean.' },
      { time: 552, text: 'Midfoot land and roll. Quiet feet.' },
      { time: 564, text: 'Arms drive gently. Cheek to pocket. Elbows close.' },
      { time: 576, text: 'Breathe in nose, out mouth. Calm and steady.' },
      { time: 588, text: 'Hold conversation pace. You can talk as you jog.' },
      { time: 600, text: 'Eyes forward. Shoulders down. Hands soft.' },
      { time: 612, text: 'Walk easy for 20 seconds. Calm breath.' },

      // 18:00-21:00 Breathing and rhythm focus
      {
        time: 624,
        text: 'Breathing set. In for 3, out for 3 while walking easy.',
      },
      { time: 636, text: 'Add quick steps for 4 counts, then easy walk.' },
      {
        time: 648,
        text: 'Form reminder. Head over hips, hips over feet. Two tracks.',
      },
      { time: 660, text: 'Stand tall for 3, 2, 1. Calm face.' },
      { time: 672, text: 'Walk a little faster. Short steps under hips.' },
      { time: 684, text: 'Arms swing smooth. Cheek to pocket.' },
      { time: 696, text: 'Breathe steady. Quiet feet.' },
      { time: 708, text: 'Ready for endurance set.' },

      // 21:00-24:00 Endurance set 3 minutes continuous easy
      { time: 720, text: 'Start easy jog. Settle into a smooth rhythm.' },
      { time: 732, text: 'Tall spine, tiny ankle lean, small quick steps.' },
      { time: 744, text: 'Midfoot land and roll. Heels kiss the ground.' },
      { time: 756, text: 'Arms drive gently. Elbows close. Hands soft.' },
      { time: 768, text: 'Breathing. In for 3, out for 3. Steady and calm.' },
      { time: 780, text: 'Eyes forward. Head over hips. Shoulders down.' },
      { time: 792, text: 'Quiet feet. Steps under your body.' },
      { time: 804, text: 'Hold this easy pace. You can talk.' },
      { time: 816, text: 'Posture check. Two tracks, knees over toes.' },
      { time: 828, text: 'Arms smooth. Cheek to pocket.' },
      { time: 840, text: 'Keep breathing steady. Calm face.' },
      { time: 852, text: 'Stay light. Heels light. Steps small.' },

      // 24:00-26:00 Run walk ladder light
      { time: 864, text: 'Jog light for 40 seconds. Smooth arms, quiet feet.' },
      { time: 876, text: 'Tall spine. Tiny lean from ankles.' },
      { time: 888, text: 'Midfoot land. Roll forward. Hands soft.' },
      { time: 900, text: 'Walk easy for 20 seconds. Breathe steady.' },
      { time: 912, text: 'Jog light for 40 seconds. Same smooth form.' },
      { time: 924, text: 'Eyes forward. Shoulders down.' },
      { time: 936, text: 'Walk easy for 20 seconds. Calm breath.' },
      { time: 948, text: 'Stand tall for 3, 2, 1.' },

      // 26:00-28:00 Stride pickups safe
      {
        time: 960,
        text: 'Stride definition. Short fast burst with smooth arms and soft landings, then easy jog.',
      },
      {
        time: 972,
        text: 'Do a 6 count fast stride in place, then 6 count easy jog.',
      },
      { time: 984, text: 'Repeat one more time. Fast 6, then easy 6.' },
      { time: 996, text: 'Reminder. Small steps, land under you, arms drive.' },
      { time: 1008, text: 'Easy jog. Conversation pace. Calm breath.' },
      { time: 1020, text: 'Midfoot land. Quiet feet. Hands soft.' },
      { time: 1032, text: 'Stand tall. Walk easy for 20 seconds.' },
      { time: 1044, text: 'Posture scan. Two tracks, tall spine.' },

      // 28:00-30:00 Cooldown and finish
      { time: 1056, text: 'Walk easy. Breathe in slow. Breathe out slow.' },
      {
        time: 1068,
        text: 'Calf stretch small. Step one foot back, heel heavy. Hold for 3, 2, 1. Switch.',
      },
      {
        time: 1080,
        text: 'Thigh stretch small. Heel to bottom if safe. Hold for 3, 2, 1. Switch.',
      },
      { time: 1092, text: 'Ankles roll. Right two, left two. Quiet feet.' },
      {
        time: 1104,
        text: 'Coach kudos. Smooth arms, quick light steps, steady breath.',
      },
      { time: 1116, text: 'Stand proud. Hold still for 3, 2, 1.' },

      // Calm breath to 30:00
      { time: 1128, text: 'Calm breath in.' },
      { time: 1140, text: 'Calm breath out.' },
      { time: 1152, text: 'Relax shoulders.' },
      { time: 1164, text: 'Relax hands.' },
      { time: 1176, text: 'Head over hips. Tall spine.' },
      { time: 1188, text: 'Quiet feet. Soft knees.' },
      { time: 1200, text: 'Breathe in for 3, 2, 1.' },
      { time: 1212, text: 'Breathe out for 3, 2, 1.' },
      { time: 1224, text: 'Small smile. Calm face.' },
      { time: 1236, text: 'Hold still for 3, 2, 1.' },
      { time: 1248, text: 'Stand tall. Thank your body.' },
      { time: 1260, text: 'Ready to hydrate.' },
      { time: 1272, text: 'Feet steady. Hands by sides.' },
      { time: 1284, text: 'Breathe steady. You did great.' },
      {
        time: 1296,
        text: 'Session complete. Beginner Endurance finished. Great job today.',
      },
    ],
    timeTotal: 1299,
    equipment: 'None',
    requiresPartner: false,
    minPlayers: 1,
  },

  // INTERMEDIATE ENDURANCE (45 minutes)
  {
    id: 2117,
    name: 'Intermediate Endurance',
    categories: ['Endurance', 'intermediate'],
    sports: ['running-technique'],
    difficulty: 'intermediate',
    skill: 'Endurance',
    points: 45,
    instructions:
      'Intermediate Endurance teaches you how to run longer with smooth form and calm pacing. This 45-minute session builds posture, arm swing, midfoot land, ankle lean, and steady breathing with longer easy runs, simple tempo waves, and safe run and walk intervals.',
    // Replace timedInstructions with:
    timedInstructions: [
      // 0:00-3:00 Arrive, posture, breath
      {
        time: 0,
        text: 'Stand tall in Ready. Feet on two tracks, knees soft, head over hips. Breathe in your nose, out your mouth.',
      },
      {
        time: 12,
        text: 'Posture definition. Tall spine, chest calm, tummy snug, shoulders down, eyes forward.',
      },
      {
        time: 24,
        text: 'Feet check. Toes point straight ahead. Knees over toes.',
      },
      {
        time: 36,
        text: 'Arms easy. Elbows bent, hands soft, swing cheek to pocket by your sides.',
      },
      { time: 48, text: 'Breathe in for 3, 2, 1. Breathe out for 3, 2, 1.' },
      { time: 60, text: 'Ankles roll. Right two, left two. Quiet feet.' },
      {
        time: 72,
        text: 'March in place. Lift knees tiny. Land soft under hips.',
      },
      {
        time: 84,
        text: 'Posture reminder. Head over hips, hips over feet. Two tracks.',
      },
      { time: 96, text: 'Stand tall. Hold for 3, 2, 1. Calm breath.' },
      { time: 108, text: 'Arms swing smooth. Hands soft. Shoulders down.' },
      { time: 120, text: 'Ready to move with form.' },
      { time: 132, text: 'Small smile. Eyes forward.' },

      // 3:00-6:00 Walk warm up and breathing rhythm
      {
        time: 144,
        text: 'Start a gentle walk. Short steps under your body. Arms swing smooth.',
      },
      {
        time: 156,
        text: 'Breathing pattern. In for 3, out for 3. Keep it steady.',
      },
      { time: 168, text: 'Walk and breathe. Quiet feet. Head over hips.' },
      { time: 180, text: 'Two tracks under feet. Toes forward. Knees soft.' },
      { time: 192, text: 'Stand tall for 3, 2, 1. Then walk again.' },
      {
        time: 204,
        text: 'Midfoot walk. Touch the middle of your foot, then roll to the front.',
      },
      { time: 216, text: 'Arms swing cheek to pocket. Elbows close.' },
      { time: 228, text: 'Breathe steady. Calm face.' },

      // 6:00-10:00 Form set: midfoot, lean, cadence
      {
        time: 240,
        text: 'Midfoot land definition. Touch the middle of your foot first, then roll to the front. Heels kiss the ground.',
      },
      { time: 252, text: 'Practice midfoot. Step and roll. Quiet feet.' },
      {
        time: 264,
        text: 'Lean definition. Tip a tiny bit from ankles, not from your waist. Body stays like a straight line.',
      },
      {
        time: 276,
        text: 'Try tiny lean with walking. Heels light. Steps small.',
      },
      {
        time: 288,
        text: 'Cadence definition. Quick small steps that are even and light.',
      },
      { time: 300, text: 'Quick steps for 4 counts, then easy walk.' },
      {
        time: 312,
        text: 'Form reminder. Tall spine, tiny lean, midfoot touch and roll.',
      },
      { time: 324, text: 'Stand tall. Hold for 3, 2, 1.' },
      { time: 336, text: 'Breathe in for 3, out for 3. Shoulders down.' },
      { time: 348, text: 'Ready to jog easy.' },

      // 10:00-16:00 Easy continuous jog 6 minutes
      {
        time: 360,
        text: 'Start an easy jog. Conversation pace means you can talk while you jog.',
      },
      {
        time: 420,
        text: 'Form check. Tall spine, tiny ankle lean, small steps under hips.',
      },
      { time: 480, text: 'Feet quiet. Land midfoot and roll forward.' },
      { time: 540, text: 'Arms smooth. Cheek to pocket. Hands soft.' },
      { time: 600, text: 'Breathing. In for 3, out for 3. Calm and steady.' },
      { time: 660, text: 'Eyes forward. Head over hips. Shoulders down.' },
      { time: 720, text: 'Hold easy pace. Quiet landings. Two tracks.' },
      { time: 780, text: 'Walk easy for 20 seconds. Calm breath.' },

      // 16:00-20:00 Tempo wave 2 easy, 2 moderate
      {
        time: 800,
        text: 'Tempo wave definition. Easy means calm talk pace. Moderate means a little faster but still smooth.',
      },
      { time: 812, text: 'Start easy jog for 2 minutes. Form first.' },
      {
        time: 932,
        text: 'Shift to moderate for 2 minutes. Steps stay small. Arms drive a little more.',
      },
      { time: 1052, text: 'Back to easy for 20 seconds. Breathe steady.' },
      {
        time: 1064,
        text: 'Form reminder. Tall spine, tiny lean, midfoot touch, hands soft.',
      },

      // 20:00-25:00 Run walk intervals 3 and 1, twice
      {
        time: 1080,
        text: 'Interval plan. Jog easy for 3 minutes, walk for 1 minute. We do it two times.',
      },
      { time: 1092, text: 'Start jog. Smooth arms. Quiet feet.' },
      { time: 1272, text: 'Walk for 1 minute. In for 3, out for 3.' },
      { time: 1332, text: 'Start jog again. Same smooth form.' },
      { time: 1512, text: 'Walk for 1 minute. Shoulders down. Calm face.' },

      // 25:00-30:00 Hill simulation and cadence drill
      {
        time: 1572,
        text: 'Hill simulation definition. A tiny extra ankle lean, shorter steps, arms pump a little more.',
      },
      {
        time: 1584,
        text: 'Hill jog 1 minute. Lean tiny. Knees lift a little. Quiet feet.',
      },
      { time: 1644, text: 'Flat easy 1 minute. Relax shoulders. Hands soft.' },
      {
        time: 1704,
        text: 'Cadence drill. Quick tiny steps for 10 counts, then normal for 10 counts. Repeat three times.',
      },
      { time: 1716, text: 'Quick 10, normal 10. Small steps under you.' },
      { time: 1756, text: 'Quick 10, normal 10. Breathe steady.' },
      { time: 1796, text: 'Quick 10, normal 10. Head over hips.' },

      // 30:00-37:00 Endurance steady 7 minutes continuous
      { time: 1812, text: 'Start steady jog for 7 minutes. Calm and smooth.' },
      {
        time: 1872,
        text: 'Form scan. Tall spine, tiny lean, midfoot touch and roll.',
      },
      { time: 1932, text: 'Arms smooth. Cheek to pocket. Elbows close.' },
      { time: 1992, text: 'Breathing steady. In for 3, out for 3.' },
      { time: 2052, text: 'Quiet landings. Steps under your body.' },
      { time: 2112, text: 'Eyes forward. Shoulders down. Hands soft.' },
      { time: 2172, text: 'Two tracks, knees over toes. Hips tall.' },
      { time: 2232, text: 'Hold the easy rhythm. Calm face.' },

      // 37:00-41:00 Stride pickups safe
      {
        time: 2292,
        text: 'Stride definition. Short fast burst with smooth arms and soft landings, then easy jog.',
      },
      {
        time: 2304,
        text: 'Do a 6 count fast stride in place, then 12 count easy jog.',
      },
      { time: 2328, text: 'Repeat a second stride. Fast 6, then easy 12.' },
      { time: 2352, text: 'Repeat a third stride. Fast 6, then easy 12.' },
      { time: 2376, text: 'Return to easy jog. Conversation pace.' },
      {
        time: 2400,
        text: 'Form reminder. Small steps, land under you, arms drive.',
      },
      { time: 2424, text: 'Breathe steady. In for 3, out for 3.' },
      { time: 2448, text: 'Walk easy for 20 seconds. Calm breath.' },

      // 41:00-44:00 Cooldown walk and mobility
      { time: 2460, text: 'Walk easy. Shake arms. Shake legs.' },
      {
        time: 2472,
        text: 'Calf stretch small. Step one foot back, heel heavy. Hold for 3, 2, 1. Switch.',
      },
      {
        time: 2484,
        text: 'Thigh stretch small. Heel to bottom if safe. Hold for 3, 2, 1. Switch.',
      },
      { time: 2496, text: 'Ankles roll. Right two, left two. Quiet feet.' },
      {
        time: 2508,
        text: 'Posture scan. Tall spine, head over hips, shoulders down.',
      },
      {
        time: 2520,
        text: 'Coach kudos. Smooth arms, quick light steps, steady breath.',
      },

      // 44:00-45:00 Calm breath close
      { time: 2532, text: 'Stand tall. Hold still for 3, 2, 1.' },
      { time: 2544, text: 'Calm breath in.' },
      { time: 2556, text: 'Calm breath out.' },
      { time: 2568, text: 'Relax shoulders. Relax hands.' },
      { time: 2580, text: 'Head over hips. Tall spine.' },
      { time: 2592, text: 'Quiet feet. Soft knees.' },
      { time: 2604, text: 'Breathe in for 3, 2, 1.' },
      { time: 2616, text: 'Breathe out for 3, 2, 1.' },
      { time: 2628, text: 'Small smile. Calm face.' },
      { time: 2640, text: 'Thank your body. It worked with focus.' },
      { time: 2652, text: 'Stand proud. Hands by sides.' },
      { time: 2664, text: 'Deep breath in. Long breath out.' },
      {
        time: 2676,
        text: 'Session complete. Intermediate Endurance finished. Great job today.',
      },
    ],
    timeTotal: 2680,
    equipment: 'None',
    requiresPartner: false,
    minPlayers: 1,
  },
];

export default runningTechniqueDrills;
